import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ticketingApi } from '@/lib/ticketing-api';
import { PageHeader } from '@/components/ui/DataTable';
import { Skeleton } from '@/components/ui/primitives';

const COLORS = ['#388eff', '#22c55e', '#eab308', '#ef4444', '#a855f7'];
const tooltip = { background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 };

export function AnalyticsPage() {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['analytics'], queryFn: ticketingApi.analytics });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('analytics.title')} subtitle={t('analytics.subtitle')} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('analytics.title')} subtitle={t('analytics.subtitle')} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('analytics.charts.openResolved')}</div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.resolutionTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" width={28} />
                <Tooltip contentStyle={tooltip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="opened" name={t('analytics.legend.opened')} stroke="#eab308" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="resolved" name={t('analytics.legend.resolved')} stroke="#22c55e" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('analytics.charts.avgResolutionTime')}</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.resolutionTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" width={28} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={tooltip} />
                <Bar dataKey="avgHours" fill="#388eff" radius={[4, 4, 0, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('analytics.charts.ticketsByStatus')}</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.ticketsByStatus} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={3}>
                  {data.ticketsByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={tooltip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('analytics.charts.vendorSlaCompliance')}</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.vendorPerformance} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis type="category" dataKey="vendor" tick={{ fill: '#e2e8f0', fontSize: 12 }} width={90} stroke="rgba(255,255,255,0.1)" />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={tooltip} />
                <Bar dataKey="slaCompliance" radius={[0, 6, 6, 0]} barSize={16}>
                  {data.vendorPerformance.map((v, i) => <Cell key={i} fill={v.slaCompliance >= 90 ? '#22c55e' : v.slaCompliance >= 80 ? '#eab308' : '#ef4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
